// frontend/src/modules/reports/pages/financial/RevenuePage.tsx
//
// Phase 4 (Reports Frontend), Task 4. Billed-vs-collected trend (shared
// BilledCollectedSeries component, DRY with CashTrendPage) + a by-client
// breakdown table, both rendered PER CURRENCY (never summed across).

import * as React from 'react';
import { ReportShell, type ReportRange } from '../../components/ReportShell';
import { DataTable } from '../../../../design-system/components/DataTable';
import { VISUAL } from '../../../../design-system/visual';
import { formatMoney } from '../../../../design-system/format';
import { reportsApi, downloadReportCsv } from '../../api';
import type { RevenueReport, RevenueGroupBy } from '../../types';
import { useReportFetch } from './useReportFetch';
import { groupByCurrency } from './currencyGroup';
import { BilledCollectedSeries } from './BilledCollectedSeries';

const GROUP_BYS: RevenueGroupBy[] = ['week', 'month'];

export function RevenuePage() {
  const [range, setRange] = React.useState<ReportRange | null>(null);
  const [groupBy, setGroupBy] = React.useState<RevenueGroupBy>('month');

  const fetcher = range ? () => reportsApi.revenue({ from: range.from, to: range.to, groupBy }) : null;
  const result = useReportFetch<RevenueReport>(fetcher, [range?.from, range?.to, groupBy]);
  const forbidden = result.status === 403;

  const report = result.data;
  const rows = report?.rows ?? [];
  const byClient = report?.by_client ?? [];
  const byClientSections = groupByCurrency(byClient);

  return (
    <ReportShell
      eyebrow="Reports / Financial"
      title="Revenue"
      description="Billed vs. collected amounts over time, and by client."
      onRangeChange={setRange}
      access={!forbidden}
      accessDescription={forbidden ? (result.error ?? undefined) : undefined}
      loading={!forbidden && (range === null || result.loading)}
      error={forbidden ? null : result.error}
      onRetry={result.reload}
      isEmpty={!forbidden && range !== null && !result.loading && !result.error && rows.length === 0}
      filters={
        <div className="inline-flex items-center gap-1 p-1 rounded-full bg-muted/50 ring-1 ring-border/40">
          {GROUP_BYS.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGroupBy(g)}
              className={[
                'px-3.5 h-8 rounded-full text-[12px] font-semibold capitalize transition-colors',
                groupBy === g ? 'bg-saffron text-zinc-950' : 'text-muted-foreground hover:text-foreground',
              ].join(' ')}
            >
              {g}
            </button>
          ))}
        </div>
      }
      csv={range ? {
        filename: `revenue-${range.from}-to-${range.to}.csv`,
        onDownload: () => downloadReportCsv(
          'revenue',
          { from: range.from, to: range.to, groupBy },
          `revenue-${range.from}-to-${range.to}.csv`,
        ),
      } : null}
    >
      {report && (
        <>
          <BilledCollectedSeries rows={rows} />

          {byClientSections.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-[13px] font-semibold text-muted-foreground uppercase tracking-[0.06em]">By client</h3>
              {byClientSections.map(([currency, clientRows]) => (
                <div key={currency} className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
                  <DataTable
                    rows={clientRows}
                    rowKey={(r) => `${r.client_id}-${currency}`}
                    columns={[
                      { key: 'client', header: `Client (${currency})`, cell: (r) => <span className="font-medium text-foreground">{r.client_name}</span> },
                      { key: 'billed', header: 'Billed', cell: (r) => formatMoney(r.billed, currency) },
                      { key: 'collected', header: 'Collected', cell: (r) => formatMoney(r.collected, currency) },
                    ]}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </ReportShell>
  );
}
